import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  IconButton,
  Snackbar,
  Input,
} from '@mui/material';
import { Add as AddIcon, Favorite as FavoriteIcon, FavoriteBorder as FavoriteBorderIcon } from '@mui/icons-material';
import { apiClient } from '../../lib/apiClient';
import { formatDate, parseFirestoreDate } from '../../lib/dateUtils';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import NoChurchSelected from '../../components/common/NoChurchSelected';

function Community({ user }) {
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ content: '', image: null, imagePreview: null });
  const [uploading, setUploading] = useState(false);
  const [likingPosts, setLikingPosts] = useState(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get user's churches first to validate
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          localStorage.removeItem('selectedChurch');
          setError('Please join a church first');
          setLoading(false);
          return;
        }

        // Get selected church from localStorage and validate it
        const stored = localStorage.getItem('selectedChurch');
        let church;
        if (stored) {
          const storedChurch = JSON.parse(stored);
          // Verify the stored church is in the user's churches
          const isValidChurch = myChurches.some(c => c.id === storedChurch.id);
          if (isValidChurch) {
            church = storedChurch;
          } else {
            // Invalid church, use first available
            localStorage.removeItem('selectedChurch');
            church = myChurches[0];
            localStorage.setItem('selectedChurch', JSON.stringify(church));
            window.dispatchEvent(new Event('churchSelected'));
          }
        } else {
          // No stored church, use first one
          church = myChurches[0];
          localStorage.setItem('selectedChurch', JSON.stringify(church));
          window.dispatchEvent(new Event('churchSelected'));
        }

        setSelectedChurch(church);
        await loadPosts(church.id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  const loadPosts = async (churchId) => {
    try {
      const data = await apiClient.getPosts(churchId);
      setPosts(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenDialog = () => {
    setFormData({ content: '', image: null, imagePreview: null });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ content: '', image: null, imagePreview: null });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const handleRemoveImage = () => {
    if (formData.imagePreview) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    setFormData({
      ...formData,
      image: null,
      imagePreview: null
    });
  };

  const handleSubmit = async () => {
    if (!formData.content.trim()) {
      setError('Post content is required');
      return;
    }

    setUploading(true);
    setError('');
    
    try {
      await apiClient.createPost(selectedChurch.id, user.id, formData.content, formData.image);
      handleCloseDialog();
      setSuccess('Post created successfully');
      await loadPosts(selectedChurch.id);
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (postId, currentLikes) => {
    const isLiked = currentLikes.includes(user.id);
    
    // Optimistically update UI
    setPosts(posts.map(post => {
      if (post.id === postId) {
        const newLikes = isLiked 
          ? post.likes.filter(id => id !== user.id)
          : [...post.likes, user.id];
        return { ...post, likes: newLikes };
      }
      return post;
    }));

    setLikingPosts(new Set(likingPosts).add(postId));
    
    try {
      await apiClient.likePost(postId, user.id);
    } catch (err) {
      // Revert on error
      setError(err.message || 'Failed to like post');
      await loadPosts(selectedChurch.id);
    } finally {
      const newSet = new Set(likingPosts);
      newSet.delete(postId);
      setLikingPosts(newSet);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const formatTimestamp = (timestamp) => {
    const date = parseFirestoreDate(timestamp);
    if (!date) return 'N/A';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && error === 'Please join a church first') {
    return <NoChurchSelected user={user} />;
  }

  if (error && !selectedChurch) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <LoadingOverlay open={uploading} message="Creating post..." />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Community
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: '#d4af39',
            '&:hover': {
              backgroundColor: '#8B7148',
            },
          }}
        >
          Create Post
        </Button>
      </Box>

      {selectedChurch && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {selectedChurch.name}
        </Typography>
      )}

      {posts.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No posts yet. Be the first to share something!
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mt: 3,
            maxWidth: { xs: '100%', md: 800 },
          }}
        >
          {posts.map((post) => {
            const isLiked = post.likes && post.likes.includes(user.id);
            const isLiking = likingPosts.has(post.id);
            
            return (
              <Card key={post.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: '#d4af39',
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        mr: 2,
                      }}
                    >
                      {getInitials(post.userName)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {post.userName || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(post.createdAt)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: post.imageUrl ? 2 : 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {post.content}
                  </Typography>

                  {post.imageUrl && (
                    <CardMedia
                      component="img"
                      image={post.imageUrl}
                      alt="Post image"
                      sx={{
                        width: '100%',
                        maxHeight: 500,
                        objectFit: 'contain',
                        borderRadius: 1,
                        mb: 2,
                      }}
                    />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                    <IconButton
                      onClick={() => handleLike(post.id, post.likes || [])}
                      disabled={isLiking}
                      sx={{
                        color: isLiked ? '#d4af39' : 'inherit',
                        '&:hover': {
                          backgroundColor: 'rgba(168, 139, 91, 0.1)',
                        },
                      }}
                    >
                      {isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {post.likes ? post.likes.length : 0} {post.likes && post.likes.length === 1 ? 'like' : 'likes'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Create Post Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '95%', sm: 600 },
          },
        }}
      >
        <DialogTitle>Create Post</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              fullWidth
              multiline
              rows={6}
              label="What's on your mind?"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Share something with your church community..."
              required
              inputProps={{ maxLength: 1000 }}
            />

            <Box>
              <Input
                type="file"
                inputProps={{ accept: 'image/*' }}
                onChange={handleImageChange}
                sx={{ display: 'none' }}
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <Button variant="outlined" component="span" fullWidth>
                  {formData.image ? 'Change Image' : 'Add Image'}
                </Button>
              </label>
            </Box>

            {formData.imagePreview && (
              <Box sx={{ position: 'relative', mt: 1 }}>
                <CardMedia
                  component="img"
                  image={formData.imagePreview}
                  alt="Preview"
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{ mt: 1 }}
                >
                  Remove Image
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.content.trim() || uploading}
            sx={{
              backgroundColor: '#d4af39',
              '&:hover': {
                backgroundColor: '#8B7148',
              },
            }}
          >
            {uploading ? 'Posting...' : 'Post'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Community;

